import React, { useState, useEffect } from 'react';
import { FaTicketAlt } from 'react-icons/fa';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  orderBy,
  arrayUnion,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { AnimatePresence } from 'framer-motion'; 
import styles from '../CSS/TicketSystem.module.css';

// Componentes internos
import TicketsTable from './TicketsTable';
import CreateTicketModal from './CreateTicketModal';
import TicketDetailsModal from './TicketDetailsModal';
import ResponseTicketModal from './ResponseTicketModal';
import CloseTicketModal from './CloseTicketModal';
import FileAttachmentHandler from './FileAttachmentHandler';
import TicketFilters from './TicketFilters';

const TicketUserSystem = ({ user }) => {
  // Estados principales
  const [tickets, setTickets] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [departamentos, setDepartamentos] = useState([]);
  const [etiquetas, setEtiquetas] = useState([]);
  const [adminsDepartamento, setAdminsDepartamento] = useState([]);
  const [modalOpen, setModalOpen] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [filterCriteria, setFilterCriteria] = useState({
    estado: 'todos',
    departamento: 'todos'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState('created_at_desc');
  const [loading, setLoading] = useState(true);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Estado para controlar items por página

  // ✅ NUEVA FUNCIÓN: Enviar notificaciones automáticas de nuevo ticket
  const enviarNotificacionNuevoTicket = async (ticket, adminsDepartamento) => {
    try {
      console.log('📧 Enviando notificaciones automáticas para nuevo ticket:', ticket.ticket_id);
      
      const response = await fetch('https://truebavirtual.onrender.com/api/new-ticket/notify-new-ticket', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ticket: {
            ticket_id: ticket.ticket_id,
            titulo: ticket.titulo,
            descripcion: ticket.descripcion,
            departamento_nombre: ticket.departamento_nombre,
            created_by_name: ticket.created_by_name,
            created_by_email: ticket.created_by_email,
            id: ticket.id
          },
          adminsDepartamento: adminsDepartamento
        })
      });

      const result = await response.json();
      
      if (result.success) {
        console.log(`✅ Notificaciones enviadas: ${result.emailsSent}/${result.totalAdmins} administradores`);
      } else {
        console.warn('⚠️ Error enviando notificaciones:', result.message);
      }
      
      return result;
      
    } catch (error) {
      console.error('💥 Error de conexión al enviar notificaciones:', error);
      return { success: false, message: 'Error de conexión con el servidor de notificaciones' };
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    if (!user?.uid) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // 1) Departamentos
        const departamentosQuery = query(collection(db, 'departamentos'));
        const departamentosSnapshot = await getDocs(departamentosQuery);
        const fetchedDepartamentos = departamentosSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setDepartamentos(fetchedDepartamentos);

        // 2) Admins de departamento
        const adminsQuery = query(collection(db, 'admins_departamento'));
        const adminsSnapshot = await getDocs(adminsQuery);
        setAdminsDepartamento(
          adminsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        );

        // 3) Etiquetas
        const etiquetasQuery = query(collection(db, 'etiquetas'));
        const etiquetasSnapshot = await getDocs(etiquetasQuery);
        setEtiquetas(
          etiquetasSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        );

        // 4) Tickets - SOLO los del usuario actual con listener en tiempo real
        console.log('🔍 Configurando listener de tickets para usuario:', user.uid);
        
        const ticketsQuery = query(
          collection(db, 'tickets'),
          where('created_by', '==', user.uid),
          orderBy('created_at', 'desc')
        );
        
        const unsubscribeTickets = onSnapshot(
          ticketsQuery,
          (snapshot) => {
            const fetchedTickets = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data(),
              created_at: doc.data().created_at?.toDate() || new Date(),
              updated_at: doc.data().updated_at?.toDate() || new Date(),
              closed_at: doc.data().closed_at?.toDate() || null
            }));
            
            console.log('✅ Tickets del usuario cargados:', fetchedTickets.length);
            
            setTickets(fetchedTickets);
            setFilteredTickets(fetchedTickets);
            setLoading(false);
          },
          (error) => {
            console.error("❌ Error cargando tickets del usuario:", error);
            setLoading(false);
          }
        );

        // Timeout de seguridad
        const timeoutId = setTimeout(() => {
          console.warn('⏰ Timeout: Quitando loading por seguridad');
          setLoading(false);
        }, 10000);

        return () => {
          clearTimeout(timeoutId);
          unsubscribeTickets();
        };
        
      } catch (error) {
        console.error('💥 Error al cargar datos iniciales:', error);
        setLoading(false);
      }
    };

    const unsubscribe = fetchData();
    
    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [user?.uid]);

  // Efecto para aplicar filtros
  useEffect(() => {
    if (!tickets.length) return;
    
    let result = [...tickets];
    
    // Filtrar tickets cerrados si showClosed es false
    if (!showClosed) {
      result = result.filter(ticket => ticket.estado !== 'cerrado');
    }
    
    // Aplicar filtros existentes
    if (filterCriteria.estado !== 'todos') {
      result = result.filter(ticket => ticket.estado === filterCriteria.estado);
    }
    
    if (filterCriteria.departamento !== 'todos') {
      result = result.filter(ticket => ticket.departamento_id === filterCriteria.departamento);
    }
    
    // Aplicar búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(ticket => 
        ticket.titulo.toLowerCase().includes(query) || 
        ticket.descripcion.toLowerCase().includes(query) ||
        (ticket.ticket_id && ticket.ticket_id.toLowerCase().includes(query))
      );
    }
    
    // Aplicar ordenamiento
    if (sortOption === 'created_at_desc') {
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortOption === 'created_at_asc') {
      result.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    
    setFilteredTickets(result);
  }, [tickets, filterCriteria, searchQuery, sortOption, showClosed]);

  /* ----- Handlers ----- */
  const handleCreateTicket = async (nuevoTicket, files = []) => {
    try {
      // Obtener el siguiente número de ticket
      const counterRef = doc(db, 'counters', 'tickets');
      const counterDoc = await getDoc(counterRef);
      
      let nextValue = 1;
      
      if (counterDoc.exists()) {
        nextValue = counterDoc.data().value + 1;
        await updateDoc(counterRef, { value: nextValue });
      } else {
        await setDoc(counterRef, { value: nextValue });
      }
      
      // Generar el ID formateado
      const ticketId = `#${String(nextValue).padStart(5, '0')}`;
      
      // Subir archivos adjuntos si hay
      let adjuntos = [];
      if (files && files.length > 0) {
        adjuntos = await FileAttachmentHandler.uploadFiles(files);
      }
      
      const ticketData = {
        ...nuevoTicket,
        ticket_id: ticketId,
        created_at: Timestamp.now(),
        updated_at: Timestamp.now(),
        created_by: user.uid,
        created_by_name: user.displayName || user.email,
        created_by_email: user.email,
        estado: 'nuevo',
        assigned_to: null,
        assigned_to_name: null,
        assigned_to_email: null,
        etiquetas: [],
        adjuntos: adjuntos.map(file => ({
          id: file.id,
          nombre: file.name,
          tipo: file.type,
          tamaño: file.size,
          url: file.url,
          localUrl: file.localUrl
        })),
        historial: [
          {
            fecha: Timestamp.now(),
            accion: 'Creación de ticket',
            usuario: user.uid,
            usuario_nombre: user.displayName || user.email,
            tiene_adjuntos: adjuntos.length > 0
          }
        ]
      };
      
      // Crear el ticket en Firebase
      const docRef = await addDoc(collection(db, 'tickets'), ticketData);
      
      // Preparar datos completos para notificaciones
      const ticketCompleto = {
        ...ticketData,
        id: docRef.id
      };
      
      // Enviar notificaciones automáticas
      const adminsDelDepartamento = adminsDepartamento.filter(
        admin => admin.departamento_id === nuevoTicket.departamento_id
      );
      
      if (adminsDelDepartamento.length > 0) {
        await enviarNotificacionNuevoTicket(ticketCompleto, adminsDelDepartamento);
      } else {
        console.warn('⚠️ No hay administradores asignados a este departamento para notificar');
      }
      
      setModalOpen(null);
      console.log('✅ Ticket creado exitosamente:', ticketId);
      return { success: true, ticketId: docRef.id, ticket_id: ticketId };
    } catch (error) {
      console.error('Error al crear ticket:', error);
      return { success: false };
    }
  };

  const handleResponderTicket = async (ticket, respuesta) => {
    const ticketId = ticket.id;
    try {
      await updateDoc(doc(db, 'tickets', ticketId), {
        updated_at: Timestamp.now(),
        respuestas: arrayUnion({
          texto: respuesta,
          fecha: Timestamp.now(),
          usuario: user.uid,
          usuario_nombre: user.displayName || user.email
        }),
        historial: arrayUnion({
          fecha: Timestamp.now(),
          accion: 'Respuesta agregada',
          usuario: user.uid,
          usuario_nombre: user.displayName || user.email
        })
      });

      console.log('✅ Respuesta agregada exitosamente');
      return true;
    } catch (error) {
      console.error('Error al responder ticket:', error);
      throw error;
    }
  };

  const handleCerrarTicket = async (ticketId, resolucion) => {
    try {
      const ticketRef = doc(db, 'tickets', ticketId);
  
      await updateDoc(ticketRef, {
        estado: 'cerrado',
        closed_at: Timestamp.now(),
        updated_at: Timestamp.now(),
        resolucion,
        historial: arrayUnion({
          fecha: Timestamp.now(),
          accion: 'Ticket cerrado',
          usuario: user.uid,
          usuario_nombre: user.displayName || user.email,
          resolucion
        })
      });

      setSelectedTicket(null);
      setModalOpen(null);
      console.log('✅ Ticket cerrado exitosamente');
      return true;
    } catch (error) {
      console.error('Error al cerrar ticket:', error);
      return false;
    }
  };

  // Formatear fecha
  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  // Renderizado del componente
  return (
    <div className={styles.ticketSystemContainer}>
      {/* Header minimalista integrado */}
      

      {/* Sección de acciones principales - Solo crear ticket */}
      <div className={styles.adminActionBar}>
        <div className={styles.actionGroup}>
          <button
            className={`${styles.adminActionButton} ${styles.primaryAction}`}
            onClick={() => setModalOpen('crear_ticket')}
            title="Crear nuevo ticket"
          >
            <FaTicketAlt />
            <span>Crear Ticket</span>
          </button>
        </div>
      </div>

      {/* Componente de Filtros */}
      <TicketFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filterCriteria={filterCriteria}
        setFilterCriteria={setFilterCriteria}
        sortOption={sortOption}
        setSortOption={setSortOption}
        departamentos={departamentos}
        adminsDepartamento={adminsDepartamento}
        userRole="user"
        showClosed={showClosed}
        setShowClosed={setShowClosed}
      />

      {/* Tabla de tickets */}
      <TicketsTable
        loading={loading}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
        showPagination={true}
        filteredTickets={filteredTickets}
        departamentos={departamentos}
        adminsDepartamento={adminsDepartamento}
        formatDate={formatDate}
        userRole="user"
        onViewTicket={(ticket) => {
          setSelectedTicket(ticket);
          setModalOpen('ver_ticket');
        }}
        onRespondTicket={(ticket) => {
          setSelectedTicket(ticket);
          setModalOpen('responder_ticket');
        }}
        onCloseTicket={(ticket) => {
          setSelectedTicket(ticket);
          setModalOpen('cerrar_ticket');
        }}
      />

      {/* --- MODALES --- */}
      <AnimatePresence>
        {modalOpen === 'crear_ticket' && (
          <CreateTicketModal 
            user={user}
            departamentos={departamentos}
            adminsDepartamento={adminsDepartamento}
            onClose={() => setModalOpen(null)}
            onCreate={handleCreateTicket}
          />
        )}

        {modalOpen === 'ver_ticket' && selectedTicket && (
          <TicketDetailsModal
            ticket={selectedTicket}
            mode="ver"
            user={user}
            userRole="user"
            departamentos={departamentos}
            etiquetas={etiquetas}
            adminsDepartamento={adminsDepartamento}
            onClose={() => {
              setModalOpen(null);
              setSelectedTicket(null);
            }}
            onResponder={() => {
              setModalOpen('responder_ticket');
            }}
            onCerrar={() => {
              setModalOpen('cerrar_ticket');
            }}
          />
        )}

        {modalOpen === 'responder_ticket' && selectedTicket && (
          <ResponseTicketModal 
            ticket={selectedTicket}
            user={user}
            userRole="user"
            departamentos={departamentos}
            etiquetas={etiquetas}
            onClose={() => {
              setModalOpen(null);
              setSelectedTicket(null);
            }}
            onResponder={handleResponderTicket}
          />
        )}

        {modalOpen === 'cerrar_ticket' && selectedTicket && (
          <CloseTicketModal 
            ticket={selectedTicket}
            user={user}
            userRole="user"
            departamentos={departamentos}
            onClose={() => {
              setModalOpen(null);
              setSelectedTicket(null);
            }}
            onCerrar={handleCerrarTicket}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TicketUserSystem;